import type { AuthSession, MembershipSummary, MeResponse, Permission, SessionUser } from '@facture/contracts'
import { create } from 'zustand'

const COMPANY_KEY = 'facture-company'
const SUPPORT_KEY = 'facture-support-company'

function readStored(key: string) {
  try {
    return localStorage.getItem(key)
  } catch {
    return null
  }
}

function writeStored(key: string, value: string | null) {
  try {
    if (value === null) localStorage.removeItem(key)
    else localStorage.setItem(key, value)
  } catch {
    /* almacenamiento bloqueado: la selección vive solo en memoria */
  }
}

function readSupportCompany(): MembershipSummary | null {
  const raw = readStored(SUPPORT_KEY)
  if (!raw) return null
  try {
    const value = JSON.parse(raw) as MembershipSummary
    return value && typeof value.companyId === 'string' ? value : null
  } catch {
    return null
  }
}

type Status = 'loading' | 'authenticated' | 'anonymous'

interface SessionState {
  status: Status
  user: SessionUser | null
  memberships: MembershipSummary[]
  companyId: string | null
  /** Compañía ajena que un superadmin abrió para dar soporte (no figura entre sus membresías). */
  supportCompany: MembershipSummary | null
  setSession: (session: AuthSession | MeResponse) => void
  selectCompany: (companyId: string) => void
  /** Solo superadmin: trabaja en una compañía de la que no es miembro. */
  enterSupportCompany: (company: MembershipSummary) => void
  clear: () => void
}

function pickCompany(memberships: MembershipSummary[], preferred: string | null, supportId: string | null) {
  if (preferred && (preferred === supportId || memberships.some((m) => m.companyId === preferred)))
    return preferred
  return (memberships.find((m) => m.isDefault) ?? memberships[0])?.companyId ?? null
}

export const useSession = create<SessionState>((set, get) => ({
  status: 'loading',
  user: null,
  memberships: [],
  companyId: null,
  supportCompany: null,
  setSession: (session) => {
    // La compañía de soporte solo vale para superadmin; para el resto se descarta.
    const support = session.user.isSuperadmin ? (get().supportCompany ?? readSupportCompany()) : null
    const companyId = pickCompany(
      session.memberships,
      get().companyId ?? readStored(COMPANY_KEY),
      support?.companyId ?? null,
    )
    const supportCompany = support && support.companyId === companyId ? support : null
    set({
      status: 'authenticated',
      user: session.user,
      memberships: session.memberships,
      companyId,
      supportCompany,
    })
  },
  selectCompany: (companyId) => {
    writeStored(COMPANY_KEY, companyId)
    const support = get().supportCompany
    if (support && support.companyId !== companyId) writeStored(SUPPORT_KEY, null)
    set({ companyId, supportCompany: support?.companyId === companyId ? support : null })
  },
  enterSupportCompany: (company) => {
    writeStored(COMPANY_KEY, company.companyId)
    writeStored(SUPPORT_KEY, JSON.stringify(company))
    set({ companyId: company.companyId, supportCompany: company })
  },
  clear: () => {
    writeStored(SUPPORT_KEY, null)
    set({ status: 'anonymous', user: null, memberships: [], companyId: null, supportCompany: null })
  },
}))

export function useActiveMembership() {
  return (
    useSession(
      (s) =>
        s.memberships.find((m) => m.companyId === s.companyId) ??
        (s.supportCompany?.companyId === s.companyId ? s.supportCompany : null),
    ) ?? null
  )
}

/** true cuando un superadmin trabaja en una compañía de la que no es miembro. */
export function useSupportMode() {
  return useSession(
    (s) =>
      s.user?.isSuperadmin === true &&
      s.companyId !== null &&
      !s.memberships.some((m) => m.companyId === s.companyId),
  )
}

export function useCan(...permissions: Permission[]) {
  const membership = useActiveMembership()
  const isSuperadmin = useSession((s) => s.user?.isSuperadmin ?? false)
  if (isSuperadmin) return true
  if (!membership) return false
  return permissions.every((p) => membership.permissions.includes(p))
}
